import { REFUND_FUNCTIONS } from '../config/functionRoutes';
import { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface DepositOperation {
  registrationId: string; name: string; state: string; amountOre: number | null;
  method: string; note: string; actor: string; emailQueued: boolean; emailError: string;
  error: string; pendingReason: string; monitorBlocked: boolean; updatedAt: string;
}
export interface DepositMonitorSnapshot { editionId: string; checkedAt: string; operations: DepositOperation[] }
export const isActiveDeposit = (op: DepositOperation) => !op.monitorBlocked && ['pending', 'processing', 'needs_review'].includes(op.state);
export const depositStateLabel = (state: string) => ({
  succeeded: 'Fullført', manual_completed: 'Manuelt oppgjort', pending: 'Venter på Stripe',
  processing: 'Behandles', needs_review: 'Krever avklaring', failed: 'Feilet', excluded: 'Unntatt',
}[state] || state);
const call = async (data: Record<string, unknown>) => (await httpsCallable(getFunctions(undefined, 'europe-west1'), REFUND_FUNCTIONS.deposit, { timeout: 540000 })(data)).data as any;

/** Page-scoped monitoring: read status, then reconcile EXISTING refunds only. */
export function useDepositRefundMonitor(editionId: string | undefined, paused: boolean) {
  const [snapshot, setSnapshot] = useState<DepositMonitorSnapshot | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  useEffect(() => { setSnapshot(null); setError(''); }, [editionId]);
  useEffect(() => {
    const currentGeneration = ++generation.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const active = () => !cancelled && generation.current === currentGeneration;
    const check = async () => {
      setChecking(true);
      let again = false;
      try {
        let status: DepositMonitorSnapshot = await call({ action: 'status', editionId });
        if (!active()) return;
        if (enabled) {
          // Sequential calls and a timeout scheduled AFTER completion prevent overlap.
          const ongoing = status.operations.filter(isActiveDeposit);
          for (const op of ongoing) {
            if (!active()) return;
            await call({ action: 'observe', editionId, registrationId: op.registrationId });
          }
          if (ongoing.length) status = await call({ action: 'status', editionId });
        }
        if (!active()) return;
        setSnapshot(status); setError(''); failures = 0;
        again = enabled && status.operations.some(isActiveDeposit);
      } catch (err) {
        if (!active()) return;
        setError((err as Error).message); failures += 1;
        again = enabled;
      } finally {
        if (active()) {
          setChecking(false);
          if (again) timer = setTimeout(check, Math.min(30000 * Math.pow(2, failures), 300000));
        }
      }
    };
    setChecking(false);
    if (editionId?.startsWith('kutc-') && !paused) void check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [editionId, enabled, paused, revision]);
  return {
    snapshot: snapshot?.editionId === editionId ? snapshot : null, checking,
    error, enabled, setEnabled, refresh: () => setRevision(value => value + 1),
  };
}
